import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import type { Theme } from '@mui/material/styles'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box'
  },
  icon: {
    marginRight: theme.spacing(2),
    fontSize: 24,
    flexShrink: 0
  },
  message: {
    fontSize: '1rem',
    lineHeight: 1.5,
    fontWeight: 700
  },
  title: {
    fontWeight: 1000,
    marginBottom: theme.spacing(0.5)
  },
  // Estilos para warning
  warningRoot: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ffc107'
  },
  warningIcon: {
    color: '#ff9800'
  },
  warningText: {
    color: '#db9730'
  },
  // Estilos para error
  errorRoot: {
    backgroundColor: '#ffebee',
    border: '1px solid #f44336'
  },
  errorIcon: {
    color: '#f44336'
  },
  errorText: {
    color: '#c62828'
  },
  // Estilos para success
  successRoot: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50'
  },
  successIcon: {
    color: '#4caf50'
  },
  successText: {
    color: '#5C8E2F'
  },
  // Estilos para info
  infoRoot: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3'
  },
  infoIcon: {
    color: '#2196f3'
  },
  infoText: {
    color: '#1565c0'
  }
}))

type Severity = 'warning' | 'error' | 'success' | 'info'

interface CustomAlertProps {
  message?: string | React.ReactNode
  title?: string
  severity?: Severity
  icon?: React.ReactElement
  maxWidth?: number | string
  classes?: {
    root?: string
    icon?: string
    message?: string
    title?: string
  }
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  message = 'Error al intentar obtener la información.',
  title,
  severity = 'warning',
  icon,
  maxWidth,
  classes: customClasses
}) => {
  const defaultClasses = useStyles()

  // Mapeo de íconos por defecto según severity
  const defaultIcons: Record<Severity, React.ReactElement> = {
    warning: <WarningIcon />,
    error: <ErrorIcon />,
    success: <CheckCircleIcon />,
    info: <InfoIcon />
  }

  const IconComponent = icon || defaultIcons[severity]

  // Clases según severity
  const rootClass =
    customClasses?.root ||
    `${defaultClasses.root} ${defaultClasses[`${severity}Root`]}`

  const iconClass =
    customClasses?.icon ||
    `${defaultClasses.icon} ${defaultClasses[`${severity}Icon`]}`

  const messageClass =
    customClasses?.message ||
    `${defaultClasses.message} ${defaultClasses[`${severity}Text`]}`

  const titleClass =
    customClasses?.title ||
    `${defaultClasses.title} ${defaultClasses[`${severity}Text`]}`

  return (
    <Box mt={1} className={rootClass} style={{ maxWidth: maxWidth }}>
      <Box component='span' className={iconClass}>
        {IconComponent}
      </Box>
      <Box className={messageClass}>
        {title && (
          <Box className={titleClass}>
            <Typography>{title}</Typography>
          </Box>
        )}
        <Typography>{message}</Typography>
      </Box>
    </Box>
  )
}

export default CustomAlert
